import React, { useEffect } from "react";
import { Redirect, useHistory, SelectedTenant, useTenants } from "polyrouter";
import { Loader } from "elements";
import {
  SelectedTenantContext,
  SelectedTenantValues
} from "polyrouter/SelectedTenantProvider";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryParamString } from "polyrouter";
import { useSelector } from "react-redux";
import { State } from "store";
import { DISMISS, useNotification } from "collections/Notifications";
import { noLogoutOnAuthErrorContext } from "polygraphql";
import { useTenantQuery } from "generated/graphql";

function useSelectedTenant(): SelectedTenantValues {
  const [thisTenantId, setThisTenantId] = useQueryParamString<SelectedTenant>(
    "tenantId"
  );
  const {
    tenant: thisTenant,
    fetching: thisFetching,
    error: thisError,
    executor: thisExecutor
  } = useTenant(thisTenantId ?? "");
  const lastTenantId = useSelector((state: State) => {
    return state.userMeta?.lastTenantId;
  });
  const { tenant: lastTenant, fetching: lastFetching } = useTenant(
    lastTenantId ?? ""
  );
  const {
    data: tenantsData,
    fetching: tenantsFetching,
    error: tenantsError,
    executor: tenantsExecutor
  } = useTenants();
  const [addNotification] = useNotification();
  const history = useHistory();

  useEffect(() => {
    const tenantDataHasStabilized = !thisFetching || !thisTenantId;
    const lastTenantDataHasStabilized = !lastFetching || !lastTenantId;
    const userTenants = tenantsData?.tenants;
    if (
      !thisTenant &&
      tenantDataHasStabilized &&
      lastTenantDataHasStabilized &&
      userTenants
    ) {
      const fixTenant =
        lastTenant ?? userTenants.find(t => t.id !== thisTenantId);
      if (fixTenant) {
        // Only redirect to home & show the toast if the user already had a (wrong or inaccessible) tid in the url
        if (thisTenantId) {
          if (window.location.pathname !== "/home") {
            history.push("/home");
          }
          addNotification({
            id: "changing-selected-tenant",
            type: "info",
            message: `Changing the tenant to ${fixTenant.name}`,
            dismiss: DISMISS.SLOW,
            persistThroughNav: true
          });
        }
        setThisTenantId(fixTenant.id);
      }
    }
  }, [
    thisTenant,
    thisFetching,
    thisTenantId,
    lastFetching,
    lastTenantId,
    tenantsData,
    setThisTenantId,
    history,
    lastTenant,
    addNotification
  ]);

  return {
    tenant: thisTenant,
    fetching: thisFetching,
    error: thisError,
    refetch: () => {
      tenantsExecutor({ requestPolicy: "network-only" });
      thisExecutor({ requestPolicy: "network-only" });
    },
    tenants: {
      tenants: tenantsData,
      fetching: tenantsFetching,
      error: tenantsError
    }
  };
}

function useTenant(id: string) {
  const { isAuthenticated } = useAuth0();
  const userAuthenticated =
    process.env.NODE_ENV === "test" ? true : isAuthenticated;
  const noTenantId = id === "";
  const { data, fetching, error, executor } = useTenantQuery({
    variables: {
      id: id
    },
    pause: !userAuthenticated || noTenantId,
    context: noLogoutOnAuthErrorContext,
    requestPolicy: "cache-and-network"
  });

  // When tenantId is not provided, query will be paused but it may still return some old tenant data, which is not desirable
  return {
    tenant: noTenantId ? undefined : data?.tenant,
    fetching,
    error,
    executor
  };
}

const TenantsProvider: React.FC = ({ children }) => {
  const selectedTenant = useSelectedTenant();
  const {
    tenant,
    error: tenantError,
    fetching: tenantFetching,
    tenants: { tenants, fetching: tenantsFetching, error: tenantsError }
  } = selectedTenant;
  const error = tenantError || tenantsError;
  const fetching = tenantFetching || tenantsFetching;
  const { push, location } = useHistory();
  useEffect(() => {
    const noTenants = (tenants?.tenants?.length ?? 0) === 0;
    if (
      !tenantsError &&
      !tenantsFetching &&
      !tenant &&
      noTenants &&
      location.pathname !== "/accept-invite"
    ) {
      push("/complete-profile");
    }
  }, [tenants, tenantsError, tenant, tenantsFetching, push, location.pathname]);
  return !fetching && error && !tenants && !tenant ? (
    <Redirect to="/error" />
  ) : (
    <SelectedTenantContext.Provider value={selectedTenant}>
      <Loader className="theme-independent" loading={!tenant && fetching}>
        {children}
      </Loader>
    </SelectedTenantContext.Provider>
  );
};

export default TenantsProvider;
